'use client';

import { Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Controller, useFieldArray, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SetupProductVariantCard } from '@/components/setup/experience/SetupProductVariantCard';
import {
  SETUP_SOFT_INPUT_CLASS,
  SetupStackedFormField,
} from '@/components/setup/experience/SetupStackedFormField';
import type { SetupManualProductsFormData } from '@/components/setup/setupManualSchemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface SetupProductFormCardProps {
  index: number;
  currency: string;
  canRemove: boolean;
  onRemove: (index: number) => void;
}

/** One product block — stacked name/description fields + inline variants list. */
export function SetupProductFormCard({ index, currency, canRemove, onRemove }: SetupProductFormCardProps) {
  const { t } = useTranslation('setup');
  const f = 'experience.connect.productForm';
  const { control } = useFormContext<SetupManualProductsFormData>();
  const { errors } = useFormState({ control, name: [`products.${index}.name`] });
  const nameError = errors.products?.[index]?.name?.message;

  const {
    fields: variantFields,
    append: appendVariant,
    remove: removeVariant,
  } = useFieldArray({ control, name: `products.${index}.variants` });

  const fieldId = `setup-product-${index}`;

  return (
    <div className="flex flex-col gap-6 py-8 first:pt-0 last:pb-0">
      {canRemove ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground m-0 text-xs font-semibold uppercase tracking-[0.08em]">
            {t(`${f}.productLabel`, { index: index + 1 })}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onRemove(index)}
          >
            <Trash2 className="size-4" aria-hidden />
            {t(`${f}.removeProduct`)}
          </Button>
        </div>
      ) : null}

      <SetupStackedFormField
        label={t(`${f}.nameLabel`)}
        htmlFor={`${fieldId}-name`}
        required
        hint={t(`${f}.nameHint`)}
        error={nameError}
      >
        <Controller
          name={`products.${index}.name`}
          control={control}
          render={({ field }) => (
            <Input
              id={`${fieldId}-name`}
              {...field}
              value={field.value || ''}
              placeholder={t(`${f}.namePlaceholder`)}
              className={cn(SETUP_SOFT_INPUT_CLASS, 'h-12 px-4 text-[0.9375rem]')}
              aria-required
              aria-invalid={!!nameError}
              aria-describedby={nameError ? `${fieldId}-name-error` : undefined}
            />
          )}
        />
      </SetupStackedFormField>

      <SetupStackedFormField label={t(`${f}.descriptionLabel`)} htmlFor={`${fieldId}-description`}>
        <Controller
          name={`products.${index}.description`}
          control={control}
          render={({ field }) => (
            <Textarea
              id={`${fieldId}-description`}
              {...field}
              value={field.value || ''}
              placeholder={t(`${f}.descriptionPlaceholder`)}
              rows={3}
              className={cn(SETUP_SOFT_INPUT_CLASS, 'resize-y px-4 py-3 text-[0.9375rem]')}
            />
          )}
        />
      </SetupStackedFormField>

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-foreground m-0 text-[0.9375rem] font-semibold leading-tight">
              {t(`${f}.variantsLabel`)}
            </p>
            <p className="text-muted-foreground m-0 mt-1 text-[0.8125rem] leading-[1.5]">
              {t(`${f}.variantsHint`)}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => appendVariant({ name: '', imageUrl: '', sku: '', priceAmount: '' })}
          >
            <Plus className="size-4" aria-hidden />
            {t(`${f}.addVariant`)}
          </Button>
        </div>

        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {variantFields.map((variantField, variantIndex) => (
            <SetupProductVariantCard
              key={variantField.id}
              productIndex={index}
              variantIndex={variantIndex}
              currency={currency}
              canRemove={variantFields.length > 1}
              onRemove={removeVariant}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}
